/**
*
* useTopicMessages
*
*/

import { useCallback, useEffect, useMemo } from 'react';
import useIsOwn from 'utils/useIsOwn.js';
import { differenceInMinutes, parseISO } from 'utils/date-fns/index.js';
import usePolling from 'utils/usePolling.js';
import useApi3 from 'utils/useApi3/index.js';
import splitMessages from '../splitMessages.js';
import { LONG_POLL, PAGESIZE, POLL_RECENT, SHORT_POLL } from '../constants.jsx';
import useConversationsContext from '../useConversationsContext.js';

function useTopicMessages() {
  const { baseUrl, topic, setInTopics } = useConversationsContext();
  const setMessages = (messages) => setInTopics([topic.id, 'messages'], messages);
  const url = topic.id && `${baseUrl}/${topic.id}/messages?offset=0&limit=${PAGESIZE}`;
  const topicMessages = topic.messages || { loading: true, initial: true };
  const loadMessages = useApi3({ url, skipParseDates: true }, setMessages);
  const { loading, updating, error, sinceUrl, refTime, totalCount } = topicMessages;

  const ready = !loading && !updating && !error;

  const originalCount = useMemo(() => totalCount, [topic.id, totalCount !== undefined]); // eslint-disable-line react-hooks/exhaustive-deps
  const isOwn = useIsOwn();
  const result = useMemo(() => splitMessages(topicMessages, originalCount, isOwn), [topicMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  function getPollTime() {
    if (!topic.permissions?.activeParticipant || !refTime?.toString().startsWith('20')) {
      return null;
    }
    const now = new Date();
    const period = differenceInMinutes(now, parseISO(result.lastTime));
    return period < POLL_RECENT ? SHORT_POLL : LONG_POLL;
  }

  const poll = useCallback(() => {
    const nextUrl = sinceUrl || `${baseUrl}/${topic.id}/messages?poll=true&limit=${PAGESIZE}&since=${refTime.toString()}`;
    loadMessages({ url: nextUrl, busyProp: 'updating', noRetry: true, ignore: [401] });
  }, [topic.id, sinceUrl, refTime]); // eslint-disable-line react-hooks/exhaustive-deps

  usePolling(poll, getPollTime(), true);

  useEffect(() => {
    if (ready) {
      const { unseenCount, parentTopicId } = topic;
      setInTopics([topic.id, 'unseenCount'], 0);
      if (unseenCount && parentTopicId) {
        setInTopics([parentTopicId, 'unseenCount'], (v) => Math.max(v - unseenCount, 0));
      }
    }
  }, [url, ready, topic]); // eslint-disable-line react-hooks/exhaustive-deps

  return result;
}

export default useTopicMessages;
